import * as React from "react";
import clsx from "clsx";
import { map, isEmpty } from "lodash";
import { Container } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import Typography from "@material-ui/core/Typography";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import BackToTrackingMapContainer from "../BackToTrackingMap";
import ArrowsCarousel from "../ArrowsCarousel";
import QuoteMessage from "../QuoteMessage";
import { getDriverImageUrl, getImageSize } from "../../utils/images";
import MoreInfo from "./MoreInfo";
import BaseInfo from "./BaseInfo";
import Compliments from "./Compliments";
import { isMobile } from "../../utils/helpers";
import { useEnvContext } from "../../env";
const useStyles = makeStyles(({
  palette,
  breakpoints,
  typography,
  transitions
}) => ({
  container: {
    fontSize: "1.2rem",
    marginTop: typography.pxToRem(136),
    maxWidth: typography.pxToRem(775),
    [breakpoints.down("sm")]: {
      fontSize: "0.875rem",
      marginTop: typography.pxToRem(80)
    }
  },
  sectionContainer: {
    marginTop: typography.pxToRem(40),
    [breakpoints.down("sm")]: {
      marginTop: typography.pxToRem(20)
    }
  },
  feedback: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: typography.pxToRem(24)
  },
  label: {
    fontSize: typography.pxToRem(22),
    fontWeight: 300,
    letterSpacing: typography.pxToRem(0.48),
    color: palette.text.headline,
    [breakpoints.down("sm")]: {
      fontSize: typography.pxToRem(16)
    }
  },
  feedbackLabel: {
    [breakpoints.up("xs")]: {
      marginBottom: typography.pxToRem(37)
    },
    [breakpoints.down("xs")]: {
      marginBottom: typography.pxToRem(15)
    }
  },
  text: {
    fontSize: typography.pxToRem(15),
    letterSpacing: typography.pxToRem(0.44),
    marginTop: typography.pxToRem(40),
    [breakpoints.down("sm")]: {
      fontSize: typography.pxToRem(13),
      marginTop: typography.pxToRem(20)
    }
  },
  quote: {
    display: "flex",
    paddingTop: typography.pxToRem(10)
  },
  feedbackContainer: {
    [breakpoints.up("sm")]: {
      marginBottom: typography.pxToRem(100)
    }
  },
  backToTracking: {
    [breakpoints.down("sm")]: {
      display: "none"
    }
  },
  expandButton: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    cursor: "pointer",
    "-webkit-tap-highlight-color": "transparent",
    "-moz-tap-highlight-color": "transparent",
    "-o-tap-highlight-color": "transparent",
    "tap-highlight-color": "transparent"
  },
  expand: {
    fontSize: typography.pxToRem(28),
    color: palette.text.headline,
    transform: "rotate(0deg)",
    marginLeft: typography.pxToRem(12),
    transition: transitions.create("transform", {
      duration: transitions.duration.shortest
    })
  },
  expandOpen: {
    transform: "rotate(180deg)"
  },
  carouselWrapper: {
    [breakpoints.down("xs")]: {
      paddingLeft: typography.pxToRem(30),
      paddingRight: typography.pxToRem(30)
    }
  }
}), {
  name: "driver-profile"
});
const responsiveCarouselMap = {
  "0": {
    items: 2
  }
};

const MoreAboutSection = ({
  driver,
  driverName
}) => {
  const styles = useStyles();
  const [open, setOpen] = React.useState(false);
  const {
    favouriteTune,
    countryCode,
    specialConsiderations
  } = driver;
  const showMoreAboutDriver = favouriteTune || countryCode || specialConsiderations;

  if (!showMoreAboutDriver) {
    return null;
  }

  return /*#__PURE__*/React.createElement("div", {
    className: styles.sectionContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.expandButton,
    onClick: () => setOpen(!open)
  }, /*#__PURE__*/React.createElement(Typography, {
    className: styles.label
  }, "More about ", driverName), /*#__PURE__*/React.createElement(ExpandMoreIcon, {
    className: clsx(styles.expand, {
      [styles.expandOpen]: open
    })
  })), open && /*#__PURE__*/React.createElement(MoreInfo, {
    driverName: driverName,
    favouriteTune: favouriteTune,
    countryCode: countryCode,
    specialConsiderations: specialConsiderations
  }));
};

const ComplimentsAndFeedbackSection = ({
  feedback,
  compliments
}) => {
  const styles = useStyles();
  const [carouselIndex, setCarouselIndex] = React.useState(0);
  const showCompliments = compliments && (compliments.deliveredWithCare || compliments.wentExtraMile || compliments.friendlyManner || compliments.respectedMyProperty);
  const showFeedback = !isEmpty(feedback);
  const slides = map(feedback, (comment, i) => /*#__PURE__*/React.createElement(QuoteMessage, {
    key: i,
    message: comment,
    className: styles.quote
  }));

  if (!showCompliments && !showFeedback) {
    return null;
  }

  return /*#__PURE__*/React.createElement("div", {
    className: clsx(styles.sectionContainer, styles.feedbackContainer)
  }, /*#__PURE__*/React.createElement(Typography, {
    className: clsx(styles.label, styles.feedbackLabel)
  }, "Compliments & Feedback"), /*#__PURE__*/React.createElement(Compliments, {
    compliments: compliments
  }), showFeedback && /*#__PURE__*/React.createElement("div", {
    className: styles.carouselWrapper
  }, /*#__PURE__*/React.createElement(ArrowsCarousel, {
    mouseTrackingEnabled: true,
    touchTrackingEnabled: true,
    dotsDisabled: true,
    buttonsDisabled: true,
    infinite: false,
    items: slides,
    responsive: responsiveCarouselMap,
    slideIndex: carouselIndex,
    onSlideIndexChange: setCarouselIndex,
    swipeDisabled: !isMobile
  })));
};

export default (props => {
  const {
    driver,
    goToTracking,
    route,
    isVehicleElectric,
    entityCode
  } = props;
  const styles = useStyles();
  const driverName = route.driverDisplayName || driver.driverName || "Driver";
  const showDriverBaseInfo = !!route.driverDisplayName || !!driver.driverName;
  const {
    API_HOST,
    ENTITY_PREFIX
  } = useEnvContext();
  const photo = getDriverImageUrl(API_HOST, ENTITY_PREFIX, entityCode, driver.driverPhoto, {
    size: getImageSize(98, 98),
    rounded: true
  });
  return /*#__PURE__*/React.createElement(BackToTrackingMapContainer, {
    onClick: goToTracking,
    className: styles.backToTracking
  }, /*#__PURE__*/React.createElement(Container, {
    maxWidth: "md",
    className: styles.container
  }, /*#__PURE__*/React.createElement(BaseInfo, {
    driver: driver,
    driverName: driverName,
    isVehicleElectric: isVehicleElectric,
    showDriverBaseInfo: showDriverBaseInfo,
    photo: photo
  }), /*#__PURE__*/React.createElement(Typography, {
    className: styles.text
  }, driver.aboutText), /*#__PURE__*/React.createElement(MoreAboutSection, {
    driver: driver,
    driverName: driverName
  }), /*#__PURE__*/React.createElement(ComplimentsAndFeedbackSection, {
    feedback: driver.feedback,
    compliments: driver.compliments
  })));
});